import { memo, useCallback, useEffect, useState } from "react";
import bpsAdminService from "../../../httpService/BpsAdminService";
import { toast } from "react-toastify";
import styles from "./LoanSummaryPage.module.scss";

export type LoanSummaryResponse = {
    amountDisbursedTotal: number;
    amountRepaidTotal: number;
    amountDisbursedRunning: number;
    expectedInterestRunning: number;
    amountDisbursedPaid: number;
    expectedInterestPaid: number;
    amountDisbursedPastDue: number;
    expectedInterestPastDue: number;
    penaltyPastDue: number;
}

type Props = {};

const LoanSummaryPage = memo((props: Props) => {
    const [loanSummaryData, setLoanSummaryData] = useState<LoanSummaryResponse>({
        amountDisbursedTotal: 0,
        amountRepaidTotal: 0,
        amountDisbursedRunning: 0,
        expectedInterestRunning: 0,
        amountDisbursedPaid: 0,
        expectedInterestPaid: 0,
        amountDisbursedPastDue: 0,
        expectedInterestPastDue: 0,
        penaltyPastDue: 0
    });

    const fetchLoanSummary = useCallback(async () => {
        try {
            const response = await bpsAdminService.getLoanSummaryData();
            setLoanSummaryData(response.data);
        }
        catch (error: any) {
            toast.error(`${error.name} - ${error.message}`);
			console.error("There was an error!", error);
        }
    }, []);

    useEffect(() => {
        fetchLoanSummary();
    }, [fetchLoanSummary]);

    return (
        <div className={styles.loanSummary}>
        <div className={styles.loanSummary__header}>Loan Section</div>
        <div className={styles.loanSummary__content}>
            <div className={styles.card}>
                <div className={styles.card__header}>
                    All Loans
                </div>
                <div className={styles.card__body}>
                    <div className={styles.card__property}>
                        <div className={styles.card__label}>
                            Amount Disbursed
                        </div>
                        <div className={styles.card__value}>
                            {loanSummaryData.amountDisbursedTotal.toLocaleString()}
                            <span className={styles.card__ngn}>NGN</span>
                        </div>
                    </div>
                    <div className={styles.card__separator}></div>
                    <div className={styles.card__property}>
                        <div className={styles.card__label}>
                            Amount Repaid
                        </div>
                        <div className={styles.card__value}>
                            {loanSummaryData.amountRepaidTotal.toLocaleString()}
                            <span className={styles.card__ngn}>NGN</span>
                        </div>
                    </div>
                    <div className={styles.card__separator}></div>
                </div>
            </div>
            <div className={styles.card}>
                <div className={styles.card__header}>
                    Running Loans
                </div>
                <div className={styles.card__body}>
                    <div className={styles.card__property}>
                        <div className={styles.card__label}>
                            Amount Disbursed
                        </div>
                        <div className={styles.card__value}>
                            {loanSummaryData.amountDisbursedRunning.toLocaleString()}
                            <span className={styles.card__ngn}>NGN</span>
                        </div>
                    </div>
                    <div className={styles.card__separator}></div>
                    <div className={styles.card__property}>
                        <div className={styles.card__label}>
                            Expected Interest
                        </div>
                        <div className={styles.card__value}>
                            {(loanSummaryData.expectedInterestRunning * 100).toLocaleString()}
                            %
                        </div>
                    </div>
                    <div className={styles.card__separator}></div>
                </div>
            </div>
            <div className={styles.card}>
                <div className={styles.card__header}>
                    Paid Loans
                </div>
                <div className={styles.card__body}>
                    <div className={styles.card__property}>
                        <div className={styles.card__label}>
                            Amount Disbursed
                        </div>
                        <div className={styles.card__value}>
                            {loanSummaryData.amountDisbursedPaid.toLocaleString()}
                            <span className={styles.card__ngn}>NGN</span>
                        </div>
                    </div>
                    <div className={styles.card__separator}></div>
                    <div className={styles.card__property}>
                        <div className={styles.card__label}>
                            Expected Interest
                        </div>
                        <div className={styles.card__value}>
                            {(loanSummaryData.expectedInterestPaid* 100).toLocaleString()}
                            %
                        </div>
                    </div>
                    <div className={styles.card__separator}></div>
                </div>
            </div>
            <div className={styles.card}>
                <div className={styles.card__header}>
                    Past Due Loans
                </div>
                <div className={styles.card__body}>
                    <div className={styles.card__property}>
                        <div className={styles.card__label}>
                            Amount Disbursed
                        </div>
                        <div className={styles.card__value}>
                            {loanSummaryData.amountDisbursedPastDue.toLocaleString()}
                            <span className={styles.card__ngn}>NGN</span>
                        </div>
                    </div>
                    <div className={styles.card__separator}></div>
                    <div className={styles.card__property}>
                        <div className={styles.card__label}>
                            Expected Interest
                        </div>
                        <div className={styles.card__value}>
                            {(loanSummaryData.expectedInterestPastDue * 100).toLocaleString()}
                            %
                        </div>
                    </div>
                    <div className={styles.card__separator}></div>
                    <div className={styles.card__property}>
                        <div className={styles.card__label}>
                            Penalty
                        </div>
                        <div className={styles.card__value}>
                            {loanSummaryData.penaltyPastDue.toLocaleString()}
                            %
                        </div>
                    </div>
                    <div className={styles.card__separator}></div>
                </div>
            </div>
        </div>
    </div>
    );
});

export default LoanSummaryPage;